/**
 * Oracle Redux Slice
 *
 * State management for Oracle Insights visualization.
 * Reference: wobbly-nibbling-chipmunk.md Part 15.4.4
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  OracleResponse,
  ExplanationTab,
  OracleUIState,
} from '../../models/oracle';

// ============================================================================
// State Interface
// ============================================================================

export interface OracleState {
  // Data from backend
  response: OracleResponse | null;

  // History: all responses for the active conversation (newest first)
  responseHistory: OracleResponse[];
  selectedHistoryIndex: number;

  // Loading & error states
  loading: boolean;
  error: string | null;

  // UI state
  ui: OracleUIState;

  // Cache for conversation-based responses (now stores arrays)
  responseCache: Record<string, OracleResponse[]>;

  // Timestamp of the last *fresh* Oracle analysis arrival. Distinct from
  // `response` updates: cache hits and hydration restores set `response` but
  // leave this null. Auto-switch listens to this signal so it only fires on
  // genuinely new runs, not on lazy-load.
  lastFreshAt: number | null;
}

// ============================================================================
// Initial State
// ============================================================================

const initialUIState: OracleUIState = {
  selectedChainIndex: 0,
  expandedNodes: [],
  activeExplanationTab: 'narrative',
  hoveredNodeId: null,
  highlightedPath: [],
};

const initialState: OracleState = {
  response: null,
  responseHistory: [],
  selectedHistoryIndex: 0,
  loading: false,
  error: null,
  ui: initialUIState,
  responseCache: {},
  lastFreshAt: null,
};

// ============================================================================
// Slice Definition
// ============================================================================

const oracleSlice = createSlice({
  name: 'oracle',
  initialState,
  reducers: {
    // -------------------------------------------------------------------------
    // Data Actions
    // -------------------------------------------------------------------------

    /**
     * Set Oracle response from API
     */
    setOracleResponse: (state, action: PayloadAction<OracleResponse>) => {
      // Save previous query before updating (for comparison)
      const previousQuery = state.response?.query;

      state.response = action.payload;
      state.loading = false;
      state.error = null;
      // Reset UI state when new response arrives
      state.ui = {
        ...initialUIState,
        // Preserve expanded state if same query
        expandedNodes: previousQuery === action.payload.query
          ? state.ui.expandedNodes
          : [],
      };
    },

    /**
     * Set Oracle responses array (history) from API or hydration
     */
    setOracleResponses: (state, action: PayloadAction<OracleResponse[]>) => {
      const responses = action.payload;
      state.responseHistory = responses;
      state.selectedHistoryIndex = 0;
      state.response = responses.length > 0 ? responses[0] : null;
      state.loading = false;
      state.error = null;
      state.ui = initialUIState;
    },

    /**
     * Select a specific history entry by index
     */
    selectHistoryEntry: (state, action: PayloadAction<number>) => {
      const index = action.payload;
      if (index >= 0 && index < state.responseHistory.length) {
        state.selectedHistoryIndex = index;
        state.response = state.responseHistory[index];
        state.ui = {
          ...initialUIState,
          // Preserve expanded state if same query
          expandedNodes: state.response?.query === state.responseHistory[index]?.query
            ? state.ui.expandedNodes
            : [],
        };
      }
    },

    /**
     * Cache responses array for a conversation
     */
    cacheResponses: (
      state,
      action: PayloadAction<{ conversationId: string; responses: OracleResponse[] }>
    ) => {
      const { conversationId, responses } = action.payload;
      state.responseCache[conversationId] = responses;
    },

    /**
     * Cache response for a conversation (legacy single-response compat)
     */
    cacheResponse: (
      state,
      action: PayloadAction<{ conversationId: string; response: OracleResponse }>
    ) => {
      const { conversationId, response } = action.payload;
      state.responseCache[conversationId] = [response];
    },

    /**
     * Load cached response
     */
    loadCachedResponse: (state, action: PayloadAction<string>) => {
      const conversationId = action.payload;
      const cached = state.responseCache[conversationId];
      if (cached && cached.length > 0) {
        state.responseHistory = cached;
        state.selectedHistoryIndex = 0;
        state.response = cached[0];
        state.error = null;
      }
    },

    /**
     * Clear Oracle response
     */
    clearOracleResponse: (state) => {
      state.response = null;
      state.responseHistory = [];
      state.selectedHistoryIndex = 0;
      state.loading = false;
      state.error = null;
      state.ui = initialUIState;
      state.lastFreshAt = null;
    },

    /**
     * Signal that a *fresh* Oracle analysis just arrived (distinct from
     * cache/hydrate restores). Auto-switch listens to this so it doesn't
     * fire when lazy-loading existing data.
     */
    markFreshOracleResponseArrived: (state) => {
      state.lastFreshAt = Date.now();
    },

    // -------------------------------------------------------------------------
    // Loading & Error Actions
    // -------------------------------------------------------------------------

    /**
     * Set loading state
     */
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },

    /**
     * Set error state
     */
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },

    // -------------------------------------------------------------------------
    // UI Actions
    // -------------------------------------------------------------------------

    /**
     * Select inference chain by index
     */
    selectChain: (state, action: PayloadAction<number>) => {
      state.ui.selectedChainIndex = action.payload;
    },

    /**
     * Toggle node expansion in the chain viewer
     */
    toggleNodeExpansion: (state, action: PayloadAction<string>) => {
      const nodeId = action.payload;
      const index = state.ui.expandedNodes.indexOf(nodeId);
      if (index === -1) {
        state.ui.expandedNodes.push(nodeId);
      } else {
        state.ui.expandedNodes.splice(index, 1);
      }
    },

    /**
     * Expand all nodes in a chain
     */
    expandAllNodes: (state, action: PayloadAction<string[]>) => {
      state.ui.expandedNodes = action.payload;
    },

    /**
     * Collapse all nodes
     */
    collapseAllNodes: (state) => {
      state.ui.expandedNodes = [];
    },

    /**
     * Set active explanation tab
     */
    setExplanationTab: (state, action: PayloadAction<ExplanationTab>) => {
      state.ui.activeExplanationTab = action.payload;
    },

    /**
     * Set hovered node for tooltip display
     */
    setHoveredNode: (state, action: PayloadAction<string | null>) => {
      state.ui.hoveredNodeId = action.payload;
    },

    /**
     * Highlight a path in the chain viewer
     */
    setHighlightedPath: (state, action: PayloadAction<string[]>) => {
      state.ui.highlightedPath = action.payload;
    },

    /**
     * Clear highlighted path
     */
    clearHighlightedPath: (state) => {
      state.ui.highlightedPath = [];
    },

    /**
     * Reset all UI state
     */
    resetUIState: (state) => {
      state.ui = initialUIState;
    },
  },
});

// ============================================================================
// Exports
// ============================================================================

export const {
  // Data actions
  setOracleResponse,
  setOracleResponses,
  selectHistoryEntry,
  cacheResponses,
  cacheResponse,
  loadCachedResponse,
  clearOracleResponse,
  markFreshOracleResponseArrived,
  // Loading & error actions
  setLoading,
  setError,
  // UI actions
  selectChain,
  toggleNodeExpansion,
  expandAllNodes,
  collapseAllNodes,
  setExplanationTab,
  setHoveredNode,
  setHighlightedPath,
  clearHighlightedPath,
  resetUIState,
} = oracleSlice.actions;

export default oracleSlice.reducer;

// ============================================================================
// Selectors
// ============================================================================

import type { RootState } from '../store';

export const selectOracleResponse = (state: RootState) => state.oracle.response;
export const selectOracleLoading = (state: RootState) => state.oracle.loading;
export const selectOracleError = (state: RootState) => state.oracle.error;
export const selectOracleUI = (state: RootState) => state.oracle.ui;
export const selectSelectedChain = (state: RootState) => {
  const { response, ui } = state.oracle;
  if (!response || !response.inference_chains.length) return null;
  return response.inference_chains[ui.selectedChainIndex] || null;
};
export const selectConfidence = (state: RootState) => state.oracle.response?.confidence || null;
export const selectExplanation = (state: RootState) => state.oracle.response?.explanation || null;
export const selectOracleHistory = (state: RootState) => state.oracle.responseHistory;
export const selectSelectedHistoryIndex = (state: RootState) => state.oracle.selectedHistoryIndex;
export const selectLastFreshAt = (state: RootState) => state.oracle.lastFreshAt;
